import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, IconButton, Paper, TextField,
    Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
    TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { InputField } from '@/pages/components/common/InputField';
import { DatePicker } from '@/pages/components/common/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import {
    GlColumnDef, GlSheetRowVO, buildColumnKey, saveGlPreview,
} from '@/services/accountService/glInvoiceService';
import type { DrCrItem, InvoiceEntryRow } from '@/pages/components/account/gl-invoice/create-stepper/context';
import { makeEmptyRow, makeEmptyDrCrItem, splitDrCrSides } from '@/pages/components/account/gl-invoice/create-stepper/context';
import {
    computeSummaryCurrentPeriod,
    editRowsToGlSheetRows,
    findDeletedInvoiceIds,
    glRowsToEditRows,
} from '@/utils/glSheetEditHelpers';
import { isFxOriginalColumn, stripDupSuffix } from '@/pages/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import { useColumnZhLookup } from '@/pages/components/account/gl-invoice/columnZhFallback';
import { ColumnSearchSelect, DR_CR_LINE_HEIGHT } from '@/pages/components/account/gl-invoice/ColumnSearchSelect';
import {
    collectInvoiceFieldHistory,
    HistorySuggestField,
    type InvoiceFieldHistory,
} from '@/pages/components/account/gl-invoice/HistorySuggestField';
import { fetchLeafGlCategories, type GlCategory } from '@/services/accountService/glCategoryService';
import { listAccountsByCompany, type GlAccount } from '@/services/accountService/companyGLCategoryService';
import {
    buildDrCrAccountLabels,
    buildDrCrColumnMapFromGlAndCoGl,
    buildDrCrColumnOptions,
} from '@/pages/components/account/gl-invoice/drCrColumnOptions';
import GlColumnLabel from '@/pages/components/account/gl-invoice/GlColumnLabel';
import { useShowClientAlias } from '@/pages/components/account/gl-invoice/useShowClientAlias';
import GlLastYearEditor from '@/pages/components/account/gl-invoice/mode/GlLastYearEditor';
import { harvestGlLastYearPrior } from '@/pages/components/account/gl-invoice/glLastYearFs';
import { editInvoiceRowMatchesQuery } from '@/pages/components/account/gl-invoice/mode/matchTinyRow';
import {
    autoFillOppositeAmount,
    balanceRowDrCrItems,
    coerceAmountInputBySide,
    computeRowDrCrBalance,
    copyDrCrAccountsFromRow,
    duplicateInvoiceEntryRow,
    filterColumnOptionsBySide,
    findLastRowWithAccounts,
    formatAmountInput,
    isEditRowUnbalanced,
    normalizeAmountOnBlur,
    shouldAdvanceAfterAmountBlur,
    shouldAdvanceAfterCrAccountSelect,
} from '@/pages/components/account/gl-invoice/mode/glEditEntryHelpers';
import { inRange, normalizeToDdMmYyyy } from '@/utils/dataUtils';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

type EditingRowKey = string | null;

interface ColumnOption {
    key: string;
    label: string;
}

function focusNextEditField(rowEl: HTMLElement | null, current: Element | null, backward = false): boolean {
    if (!rowEl) return false;
    const nodes = Array.from(
        rowEl.querySelectorAll<HTMLElement>('input:not([disabled]), textarea:not([disabled]), button:not([disabled])'),
    ).filter((el) => {
        if (el.getAttribute('data-edit-skip') === '1') return false;
        if (el.tagName === 'BUTTON' && el.getAttribute('data-edit-nav') !== '1') return false;
        return el.offsetParent != null;
    });
    if (nodes.length === 0) return false;
    const idx = current ? nodes.findIndex((n) => n === current || n.contains(current)) : -1;
    const nextIdx = backward
        ? (idx <= 0 ? nodes.length - 1 : idx - 1)
        : (idx < 0 ? 0 : idx + 1);
    if (!backward && idx >= 0 && idx >= nodes.length - 1) return false;
    nodes[nextIdx]?.focus();
    if (nodes[nextIdx] instanceof HTMLInputElement) nodes[nextIdx].select?.();
    return true;
}

interface CompactRowProps {
    row: InvoiceEntryRow;
    rowIdx: number;
    columnMap: Map<string, GlColumnDef>;
    /** 列缺中文时的标准科目/报表分类补全 */
    resolveFallbackZh: (def: GlColumnDef) => string;
    onEdit: (rowKey: string) => void;
    onDuplicate: (rowKey: string) => void;
    onRemoveRow: (key: string) => void;
    canDelete: boolean;
    showClientAlias: boolean;
    isAliasVisible: (columnKey?: string | null) => boolean;
    onToggleRevealAlias: (columnKey: string) => void;
    unbalanced?: boolean;
}

const CompactRow = React.memo(function CompactRow({
    row, rowIdx, columnMap, resolveFallbackZh, onEdit, onDuplicate, onRemoveRow, canDelete,
    showClientAlias, isAliasVisible, onToggleRevealAlias, unbalanced,
}: CompactRowProps) {
    return (
        <TableRow
            onClick={() => onEdit(row.key)}
            sx={{
                cursor: 'pointer',
                ...(unbalanced ? { bgcolor: 'error.50' } : {}),
                '&:hover': { bgcolor: unbalanced ? 'error.100' : 'action.hover' },
            }}
        >
            <TableCell sx={{ fontSize: 12, color: 'text.secondary', py: 0.5 }}>{rowIdx + 1}</TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5, whiteSpace: 'nowrap' }}>{row.date || '—'}</TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5, maxWidth: 160 }}>
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.description ?? ''}>
                    {row.description || '—'}
                </Box>
            </TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5 }}>{row.voucherNo || ''}</TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5 }}>{row.invoiceNo || ''}</TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5 }}>{row.invoiceDate || ''}</TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5 }}>{row.chequeNo || ''}</TableCell>
            <TableCell sx={{ fontSize: 13, py: 0.5, maxWidth: 120 }}>
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.details ?? ''}>
                    {row.details || ''}
                </Box>
            </TableCell>
            {(['dr', 'cr'] as const).map((side) => {
                const indexes = splitDrCrSides(row.drCrItems)[side]
                    .filter((idx) => row.drCrItems[idx]?.columnKey || row.drCrItems[idx]?.amount);
                const lineSx = { fontSize: 13, lineHeight: '22px', height: 22 } as const;
                return (
                    <React.Fragment key={side}>
                        <TableCell sx={{ py: 0.5 }} onClick={(e) => e.stopPropagation()}>
                            {indexes.map((idx) => {
                                const item = row.drCrItems[idx];
                                const col = item.columnKey ? columnMap.get(item.columnKey) : undefined;
                                if (!col) {
                                    return <Box key={idx} sx={lineSx}>—</Box>;
                                }
                                const key = item.columnKey!;
                                return (
                                    <GlColumnLabel
                                        key={idx}
                                        def={col}
                                        fallbackZh={resolveFallbackZh(col)}
                                        showAlias={isAliasVisible(key)}
                                        pageShowAlias={showClientAlias}
                                        onToggleReveal={() => onToggleRevealAlias(key)}
                                        suffix={item.exRate ? ` @ ${item.exRate}` : ''}
                                    />
                                );
                            })}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, whiteSpace: 'nowrap' }}>
                            {indexes.map((idx) => (
                                <Box key={idx} sx={lineSx}>
                                    {row.drCrItems[idx]?.amount
                                        ? coerceAmountInputBySide(side, row.drCrItems[idx].amount)
                                        : '—'}
                                </Box>
                            ))}
                        </TableCell>
                    </React.Fragment>
                );
            })}
            <TableCell>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(row.key); }} sx={{ p: 0.25 }} title="Edit row">
                    <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onDuplicate(row.key); }}
                    sx={{ p: 0.25 }}
                    title="Duplicate row (Ctrl+D)"
                >
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
                <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onRemoveRow(row.key); }}
                    disabled={!canDelete}
                    sx={{ p: 0.25, color: 'error.main' }}
                    title="Remove row"
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </TableCell>
        </TableRow>
    );
});

interface EditRowProps {
    row: InvoiceEntryRow;
    rowIdx: number;
    columnOptions: ColumnOption[];
    columnMap: Map<string, GlColumnDef>;
    /** glCode → ASSET/...，用于 DR/CR 下拉过滤 */
    typeByCode: Map<string, string>;
    onUpdateRow: (key: string, patch: Partial<InvoiceEntryRow>) => void;
    onUpdateDrCr: (rowKey: string, idx: number, patch: Partial<DrCrItem>) => void;
    onRemoveDrCr: (rowKey: string, idx: number) => void;
    onAddDrCr: (rowKey: string, side: 'dr' | 'cr') => void;
    onBalanceRow: (rowKey: string) => void;
    onDuplicate: (rowKey: string) => void;
    onRemoveRow: (key: string) => void;
    /** Done：只结束编辑，不加行 */
    onDone: () => void;
    /** Esc：只结束编辑，不加行 */
    onCancelEdit?: () => void;
    /** 选完 CR 后自动加下一行 */
    onAdvanceAfterCr: () => void;
    /** 当前是否全表最后一行（中间行改 CR 不加行） */
    isLastRow: boolean;
    onDoneAndNext: () => void;
    onSaveAll: () => void;
    canDelete: boolean;
    fieldHistory: InvoiceFieldHistory;
    periodFrom?: string | null;
    periodTo?: string | null;
}

const editCellSx = { py: 1, verticalAlign: 'top !important' } as const;
const editLineBoxSx = {
    height: DR_CR_LINE_HEIGHT,
    display: 'flex',
    alignItems: 'center',
} as const;
const datePickerWrapSx = {
    width: 140,
    '& .MuiFormControl-root': { m: 0 },
    '& .MuiTextField-root': { m: 0 },
} as const;

const EditRow = React.memo(function EditRow({
    row, rowIdx, columnOptions, columnMap, typeByCode, onUpdateRow, onUpdateDrCr, onRemoveDrCr, onAddDrCr,
    onBalanceRow, onDuplicate, onRemoveRow, onDone, onCancelEdit, onAdvanceAfterCr, isLastRow, onDoneAndNext, onSaveAll, canDelete,
    fieldHistory, periodFrom: periodFromProp, periodTo: periodToProp,
}: EditRowProps) {
    const periodFrom = normalizeToDdMmYyyy(periodFromProp) || undefined;
    const periodTo = normalizeToDdMmYyyy(periodToProp) || undefined;
    const rowRef = useRef<HTMLTableRowElement | null>(null);
    const balance = useMemo(
        () => computeRowDrCrBalance(row.drCrItems, columnMap),
        [row.drCrItems, columnMap],
    );

    const handleFieldKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            (onCancelEdit ?? onDone)();
            return;
        }
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            onSaveAll();
            return;
        }
        if ((e.ctrlKey || e.metaKey) && (e.key === 'd' || e.key === 'D')) {
            e.preventDefault();
            e.stopPropagation();
            onDuplicate(row.key);
            return;
        }
        // 金额格按 = 补平
        if (e.key === '=' && !(e.ctrlKey || e.metaKey || e.altKey)) {
            const target = e.target as HTMLElement;
            if (target.getAttribute('data-amount-field') === '1') {
                e.preventDefault();
                onBalanceRow(row.key);
                return;
            }
        }
        if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
        // Autocomplete 展开时交给 MUI 选中选项
        if ((e.target as HTMLElement).closest?.('.MuiAutocomplete-root')
            && document.querySelector('.MuiAutocomplete-listbox')) {
            return;
        }
        e.preventDefault();
        const moved = focusNextEditField(rowRef.current, e.target as Element);
        if (!moved) onDoneAndNext();
    }, [onBalanceRow, onDone, onCancelEdit, onDoneAndNext, onDuplicate, onSaveAll, row.key]);

    return (
        <TableRow
            ref={rowRef}
            onKeyDown={handleFieldKeyDown}
            sx={{ bgcolor: 'primary.50', '& > .MuiTableCell-root': { verticalAlign: 'top' } }}
        >
            <TableCell sx={{ ...editCellSx, fontSize: 12, color: 'text.secondary' }}>
                <Box sx={editLineBoxSx}>{rowIdx + 1}</Box>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <Box sx={datePickerWrapSx}>
                        <DatePicker
                            value={row.date || null}
                            onChange={(v) => onUpdateRow(row.key, { date: v ?? '' })}
                            minDate={periodFrom || undefined}
                            maxDate={periodTo || undefined}
                            size="small"
                            compact
                            align="left"
                            fullWidth
                        />
                    </Box>
                </Box>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <HistorySuggestField
                        value={row.description}
                        options={fieldHistory.description}
                        onChange={(v) => onUpdateRow(row.key, { description: v })}
                        fullWidth
                        autoFocus
                        placeholder="Description"
                    />
                </Box>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <HistorySuggestField
                        value={row.voucherNo}
                        options={fieldHistory.voucherNo}
                        onChange={(v) => onUpdateRow(row.key, { voucherNo: v })}
                        width={100}
                        placeholder="Voucher No"
                    />
                </Box>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <HistorySuggestField
                        value={row.invoiceNo}
                        options={fieldHistory.invoiceNo}
                        onChange={(v) => onUpdateRow(row.key, { invoiceNo: v })}
                        width={100}
                        placeholder="Invoice No"
                    />
                </Box>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <Box sx={datePickerWrapSx}>
                        <DatePicker
                            value={row.invoiceDate || null}
                            onChange={(v) => onUpdateRow(row.key, { invoiceDate: v ?? '' })}
                            size="small"
                            compact
                            align="left"
                            fullWidth
                        />
                    </Box>
                </Box>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <HistorySuggestField
                        value={row.chequeNo}
                        options={fieldHistory.chequeNo}
                        onChange={(v) => onUpdateRow(row.key, { chequeNo: v })}
                        width={100}
                        placeholder="Cheque No"
                    />
                </Box>
            </TableCell>
            <TableCell sx={editCellSx}>
                <Box sx={editLineBoxSx}>
                    <HistorySuggestField
                        value={row.details}
                        options={fieldHistory.details}
                        onChange={(v) => onUpdateRow(row.key, { details: v })}
                        fullWidth
                        placeholder="Details"
                    />
                </Box>
            </TableCell>
            {(['dr', 'cr'] as const).map((side) => {
                const sideIndexes = splitDrCrSides(row.drCrItems)[side];
                const canRemoveSideItem = sideIndexes.length > 1;
                const lineGap = 0.5;
                return (
                <React.Fragment key={side}>
                <TableCell sx={{ verticalAlign: 'top', py: 1 }}>
                    <Stack spacing={lineGap} alignItems="flex-start">
                        {sideIndexes.map((idx) => {
                            const item = row.drCrItems[idx];
                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        height: DR_CR_LINE_HEIGHT,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <ColumnSearchSelect
                                        options={filterColumnOptionsBySide(
                                            columnOptions,
                                            columnMap,
                                            side,
                                            typeByCode,
                                            item.columnKey,
                                        )}
                                        value={item.columnKey}
                                        onChange={(columnKey) => {
                                            const def = columnKey ? columnMap.get(columnKey) : undefined;
                                            onUpdateDrCr(row.key, idx, {
                                                columnKey,
                                                ...(!isFxOriginalColumn(def) ? { exRate: '' } : {}),
                                            });
                                            if (shouldAdvanceAfterCrAccountSelect(row.drCrItems, idx, columnKey, isLastRow)) {
                                                // Autocomplete onChange 里立刻换行会卸掉下拉，延后一拍再加行
                                                window.setTimeout(() => onAdvanceAfterCr(), 0);
                                            }
                                        }}
                                        width={260}
                                        placeholder={side === 'dr' ? 'Search DR account' : 'Search CR account'}
                                    />
                                </Box>
                            );
                        })}
                        <Button
                            size="small"
                            variant="text"
                            startIcon={<AddIcon />}
                            onClick={() => onAddDrCr(row.key, side)}
                            data-edit-skip="1"
                            sx={{ textTransform: 'none', alignSelf: 'flex-start', minWidth: 0, px: 0.5, py: 0 }}
                        >
                            Add {side.toUpperCase()}
                        </Button>
                    </Stack>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', py: 1 }}>
                    <Stack spacing={lineGap} alignItems="flex-end">
                        {sideIndexes.map((idx) => {
                            const item = row.drCrItems[idx];
                            return (
                                <Stack
                                    key={idx}
                                    direction="row"
                                    spacing={0.25}
                                    alignItems="center"
                                    justifyContent="flex-end"
                                    sx={{ height: DR_CR_LINE_HEIGHT, width: '100%' }}
                                >
                                    {isFxOriginalColumn(item.columnKey ? columnMap.get(item.columnKey) : undefined) && (
                                        <InputField
                                            margin="none"
                                            fullWidth={false}
                                            align="right"
                                            placeholder="Rate"
                                            label="Rate"
                                            value={item.exRate ?? ''}
                                            onChange={(e) => onUpdateDrCr(row.key, idx, { exRate: e.target.value })}
                                            sx={{ width: 72, minWidth: 72 }}
                                        />
                                    )}
                                    <InputField
                                        margin="none"
                                        fullWidth={false}
                                        align="right"
                                        type="money"
                                        allowNegative={false}
                                        placeholder="0.00"
                                        value={coerceAmountInputBySide(side, item.amount)}
                                        onChange={(e) => onUpdateDrCr(row.key, idx, {
                                            amount: coerceAmountInputBySide(side, e.target.value),
                                        })}
                                        onBlur={() => {
                                            const normalized = normalizeAmountOnBlur(side, item.amount);
                                            onUpdateDrCr(row.key, idx, { amount: normalized });
                                            // 科目已沿用时只填金额：失焦后借贷平衡 → 自动下一行
                                            if (shouldAdvanceAfterAmountBlur(row.drCrItems, idx, normalized, columnMap)) {
                                                onAdvanceAfterCr();
                                            }
                                        }}
                                        inputProps={{ 'data-amount-field': '1' }}
                                        sx={{ width: 96, minWidth: 96 }}
                                        title={side === 'cr' ? 'CR amount (saved as negative)' : 'DR amount'}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => onRemoveDrCr(row.key, idx)}
                                        disabled={!canRemoveSideItem}
                                        data-edit-skip="1"
                                        sx={{
                                            p: 0.25,
                                            visibility: canRemoveSideItem ? 'visible' : 'hidden',
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            );
                        })}
                        <Typography
                            variant="caption"
                            sx={{
                                color: balance.balanced ? 'success.main' : 'error.main',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                lineHeight: 1.2,
                                pr: 0.5,
                            }}
                        >
                            {side.toUpperCase()} {formatAmountInput(side === 'dr' ? balance.drTotal : balance.crTotal)}
                        </Typography>
                    </Stack>
                </TableCell>
                </React.Fragment>
                );
            })}
            <TableCell sx={{ verticalAlign: 'top', py: 1 }}>
                <Stack spacing={0.5} alignItems="stretch" sx={{ minWidth: 120 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Button
                            size="small"
                            variant="contained"
                            onClick={onDone}
                            data-edit-nav="1"
                            sx={{ textTransform: 'none' }}
                        >
                            Done
                        </Button>
                        <Tooltip title="Duplicate (Ctrl+D)">
                            <IconButton size="small" onClick={() => onDuplicate(row.key)} data-edit-skip="1">
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            size="small"
                            onClick={() => onRemoveRow(row.key)}
                            disabled={!canDelete}
                            data-edit-skip="1"
                            sx={{ color: 'error.main' }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                    <Tooltip title="Fill opposite amount (=)">
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onBalanceRow(row.key)}
                                disabled={balance.balanced || (balance.drTotal === 0 && balance.crTotal === 0)}
                                data-edit-skip="1"
                                sx={{ textTransform: 'none', width: '100%' }}
                            >
                                Balance
                            </Button>
                        </span>
                    </Tooltip>
                    <Typography
                        variant="caption"
                        sx={{
                            color: balance.balanced ? 'success.main' : 'error.main',
                            fontWeight: 600,
                            textAlign: 'center',
                        }}
                    >
                        {balance.balanced
                            ? 'Balanced'
                            : `Diff ${formatAmountInput(Math.abs(balance.diff))}`}
                    </Typography>
                </Stack>
            </TableCell>
        </TableRow>
    );
});

export interface GlSheetEditViewProps {
    companyId: number;
    annualId: number;
    columnDefs: GlColumnDef[];
    initialRows: GlSheetRowVO[];
    summaryPriorYear: Record<string, number>;
    summaryPriorYearRemarks?: Record<string, string>;
    /** 审计区间，限制行 Date 取值 */
    periodFrom?: string | null;
    periodTo?: string | null;
    onSaved: () => void;
    onCancel: () => void;
}

export default function GlSheetEditView({
    companyId, annualId, columnDefs, initialRows, summaryPriorYear,
    summaryPriorYearRemarks, periodFrom, periodTo, onSaved, onCancel,
}: GlSheetEditViewProps) {
    const initialInvoiceIds = useMemo(
        () => new Set(initialRows.map((r) => r.invoiceId).filter(Boolean)),
        [initialRows],
    );

    const auditDateMin = useMemo(
        () => normalizeToDdMmYyyy(periodFrom) || undefined,
        [periodFrom],
    );
    const auditDateMax = useMemo(
        () => normalizeToDdMmYyyy(periodTo) || undefined,
        [periodTo],
    );

    const emptySeedRef = useRef<InvoiceEntryRow | null>(null);
    const [rows, setRows] = useState<InvoiceEntryRow[]>(() => {
        const converted = glRowsToEditRows(initialRows, columnDefs);
        if (converted.length > 0) return converted;
        // 无数据进编辑时给一行空白并进入编辑态
        emptySeedRef.current = makeEmptyRow();
        return [emptySeedRef.current];
    });
    const [editingRowKey, setEditingRowKey] = useState<EditingRowKey>(() => (
        initialRows.length === 0 ? (emptySeedRef.current?.key ?? null) : null
    ));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editPanel, setEditPanel] = useState<'invoices' | 'lastYear'>('invoices');
    const [lastYearMounted, setLastYearMounted] = useState(false);
    const editedPriorRef = useRef<Record<string, number> | null>(null);
    const remarksRef = useRef<Record<string, string>>(summaryPriorYearRemarks || {});
    const [pageNum, setPageNum] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [rowQuery, setRowQuery] = useState('');
    /** 只显示借贷不平衡的发票 */
    const [unbalancedOnly, setUnbalancedOnly] = useState(false);
    /** Duplicate：沿用日期 */
    const [dupKeepDate, setDupKeepDate] = useState(true);
    const {
        showClientAlias, setShowClientAlias, isAliasVisible, toggleRevealKey,
    } = useShowClientAlias();
    /** DR/CR：标准 GL 叶子 + 本公司 Co. GL 别名（不用模板 columnDefs） */
    const [leafCategories, setLeafCategories] = useState<GlCategory[]>([]);
    const [coGlAccounts, setCoGlAccounts] = useState<GlAccount[]>([]);
    const handleSaveRef = useRef<() => void>(() => {});

    useEffect(() => {
        let cancelled = false;
        fetchLeafGlCategories()
            .then((leaves) => {
                if (cancelled) return;
                setLeafCategories(
                    (leaves ?? []).filter((c) => !!c.fsCode && !!c.code && c.isActive !== 0),
                );
            })
            .catch(() => {
                if (!cancelled) setLeafCategories([]);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (!companyId) {
            setCoGlAccounts([]);
            return;
        }
        listAccountsByCompany(companyId)
            .then((list) => {
                if (!cancelled) setCoGlAccounts(list ?? []);
            })
            .catch(() => {
                if (!cancelled) setCoGlAccounts([]);
            });
        return () => {
            cancelled = true;
        };
    }, [companyId]);

    const fallbackZh = useColumnZhLookup();

    const columnMap = useMemo(
        () => buildDrCrColumnMapFromGlAndCoGl(leafCategories, coGlAccounts),
        [leafCategories, coGlAccounts],
    );

    const columnOptions = useMemo(
        () => buildDrCrColumnOptions(columnMap, fallbackZh, showClientAlias),
        [columnMap, fallbackZh, showClientAlias],
    );

    const typeByCode = useMemo(() => {
        const m = new Map<string, string>();
        for (const c of leafCategories) {
            if (!c.code || !c.type) continue;
            m.set(c.code, c.type);
            m.set(stripDupSuffix(c.code), c.type);
        }
        return m;
    }, [leafCategories]);

    const accountLabelsByKey = useMemo(
        () => buildDrCrAccountLabels(columnMap, fallbackZh),
        [columnMap, fallbackZh],
    );

    const liveSummary = useMemo(() => computeSummaryCurrentPeriod(rows), [rows]);

    const rowQueryTrimmed = rowQuery.trim();
    const searching = rowQueryTrimmed !== '';
    const unbalancedCount = useMemo(
        () => rows.filter((r) => isEditRowUnbalanced(r.drCrItems, columnMap)).length,
        [rows, columnMap],
    );
    const filteredRows = useMemo(() => {
        let list = rows;
        if (unbalancedOnly) {
            list = list.filter((r) => isEditRowUnbalanced(r.drCrItems, columnMap));
        }
        if (searching) {
            list = list.filter((r) => editInvoiceRowMatchesQuery(r, rowQueryTrimmed, accountLabelsByKey));
        }
        return list;
    }, [rows, searching, rowQueryTrimmed, accountLabelsByKey, unbalancedOnly, columnMap]);

    useEffect(() => {
        setPageNum(0);
    }, [rowQueryTrimmed, unbalancedOnly]);

    const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    const safePage = Math.min(pageNum, pageCount - 1);
    const pageStart = safePage * pageSize;
    const pagedRows = useMemo(
        () => filteredRows.slice(pageStart, pageStart + pageSize),
        [filteredRows, pageStart, pageSize],
    );

    useEffect(() => {
        if (pageNum > pageCount - 1) {
            setPageNum(Math.max(0, pageCount - 1));
        }
    }, [pageNum, pageCount]);

    // 正在编辑的行若不在当前过滤结果 / 页，自动跳到其所在页；被过滤掉则结束编辑
    useEffect(() => {
        if (!editingRowKey) return;
        const idx = filteredRows.findIndex((r) => r.key === editingRowKey);
        if (idx < 0) {
            setEditingRowKey(null);
            return;
        }
        const targetPage = Math.floor(idx / pageSize);
        if (targetPage !== safePage) {
            setPageNum(targetPage);
        }
    }, [editingRowKey, filteredRows, pageSize, safePage]);

    const updateRow = useCallback((key: string, patch: Partial<InvoiceEntryRow>) => {
        setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    }, []);

    const addRow = useCallback(() => {
        // 清空搜索，避免新行被过滤掉看不见
        setRowQuery('');
        const newRow = makeEmptyRow();
        setRows((prev) => {
            const lastDated = [...prev].reverse().find((r) => (r.date || '').trim());
            if (lastDated?.date) newRow.date = lastDated.date;
            // 同类业务连录：沿用上一笔已选科目（金额清空）
            newRow.drCrItems = copyDrCrAccountsFromRow(findLastRowWithAccounts(prev));
            const next = [...prev, newRow];
            setPageNum(Math.floor((next.length - 1) / pageSize));
            return next;
        });
        setEditingRowKey(newRow.key);
    }, [pageSize]);

    const removeRow = useCallback((key: string) => {
        setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));
        setEditingRowKey((k) => (k === key ? null : k));
    }, []);

    const balanceRow = useCallback((rowKey: string) => {
        setRows((prev) => prev.map((r) => {
            if (r.key !== rowKey) return r;
            return { ...r, drCrItems: balanceRowDrCrItems(r.drCrItems, columnMap) };
        }));
    }, [columnMap]);

    const duplicateRow = useCallback((rowKey: string) => {
        setRowQuery('');
        setRows((prev) => {
            const idx = prev.findIndex((r) => r.key === rowKey);
            if (idx < 0) return prev;
            const copy = duplicateInvoiceEntryRow(prev[idx], {
                clearAmounts: true,
                keepDate: dupKeepDate,
            });
            const next = [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
            setPageNum(Math.floor((idx + 1) / pageSize));
            setEditingRowKey(copy.key);
            return next;
        });
    }, [dupKeepDate, pageSize]);

    const doneAndNext = useCallback(() => {
        if (!editingRowKey) {
            setEditingRowKey(null);
            return;
        }
        const idx = filteredRows.findIndex((r) => r.key === editingRowKey);
        const next = idx >= 0 ? filteredRows[idx + 1] : undefined;
        if (next) {
            setEditingRowKey(next.key);
            return;
        }
        // 最后一行 Enter：自动加一行继续连录（沿用科目）
        addRow();
    }, [editingRowKey, filteredRows, addRow]);

    const updateDrCr = useCallback((rowKey: string, idx: number, patch: Partial<DrCrItem>) => {
        setRows((prev) => prev.map((r) => {
            if (r.key !== rowKey) return r;
            const items = [...r.drCrItems];
            // 保留原 side：在 CR 列填正数金额时不要跳到 DR
            items[idx] = { ...items[idx], ...patch };
            // DR 金额/科目变化时自动填 CR（1DR+1CR 覆盖；多 CR 只填空格）
            const nextItems = (patch.amount !== undefined || patch.columnKey !== undefined)
                ? autoFillOppositeAmount(items, idx, columnMap)
                : items;
            return { ...r, drCrItems: nextItems };
        }));
    }, [columnMap]);

    const addDrCr = useCallback((rowKey: string, side: 'dr' | 'cr') => {
        setRows((prev) => prev.map((r) => {
            if (r.key !== rowKey) return r;
            return { ...r, drCrItems: [...r.drCrItems, makeEmptyDrCrItem(side)] };
        }));
    }, []);

    const removeDrCr = useCallback((rowKey: string, idx: number) => {
        setRows((prev) => prev.map((r) => {
            if (r.key !== rowKey) return r;
            const removed = r.drCrItems[idx];
            const side = removed?.side ?? 'dr';
            const items = r.drCrItems.filter((_, i) => i !== idx);
            const sides = splitDrCrSides(items);
            if (sides[side].length === 0) items.push(makeEmptyDrCrItem(side));
            return { ...r, drCrItems: items };
        }));
    }, []);

    const handleSave = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            if (auditDateMin || auditDateMax) {
                const outOfRange = rows
                    .map((r, i) => ({ r, i }))
                    .filter(({ r }) => {
                        const d = (r.date || '').trim();
                        if (!d) return false;
                        return !inRange(d, auditDateMin, auditDateMax);
                    });
                if (outOfRange.length > 0) {
                    const sample = outOfRange.slice(0, 3).map(({ r, i }) => `#${i + 1} ${r.date}`).join(', ');
                    setError(
                        `Date must be within audit period (${auditDateMin || '…'} ~ ${auditDateMax || '…'}): ${sample}`,
                    );
                    setSaving(false);
                    return;
                }
            }
            const saveRows = editRowsToGlSheetRows(rows);
            const deletedInvoiceIds = findDeletedInvoiceIds(initialInvoiceIds, saveRows);
            // Last Year 打开后：从 FS 控件 harvest，但用已有分列（Sales/Refund）作 original 保留 GL 两列
            const priorToSave = lastYearMounted
                ? harvestGlLastYearPrior(editedPriorRef.current ?? summaryPriorYear)
                : (editedPriorRef.current ?? summaryPriorYear);
            const result = await saveGlPreview({
                companyId,
                annualId,
                rows: saveRows,
                // 打开过 Last Year 时即使清空也要提交，空对象表示删掉全部上年数
                ...(lastYearMounted || Object.keys(priorToSave).length > 0
                    ? {
                        summaryPriorYear: priorToSave,
                        summaryPriorYearRemarks: remarksRef.current,
                    }
                    : {}),
                ...(deletedInvoiceIds.length > 0 ? { deletedInvoiceIds } : {}),
                replaceExisting: false,
            });
            if (result.errorCount > 0 && result.errors?.length) {
                setError(result.errors.join('; '));
                return;
            }
            onSaved();
        } catch (e: any) {
            setError(e?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    }, [rows, companyId, annualId, summaryPriorYear, initialInvoiceIds, onSaved, lastYearMounted, auditDateMin, auditDateMax]);

    useEffect(() => {
        handleSaveRef.current = () => { void handleSave(); };
    }, [handleSave]);

    // 全局 Ctrl+Enter 保存（编辑行内外均可）
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (editPanel !== 'invoices') return;
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                handleSaveRef.current();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [editPanel]);

    const canDelete = rows.length > 1;
    const validCount = rows.filter((r) => r.date || r.description || r.voucherNo).length;
    const fieldHistory = useMemo(() => collectInvoiceFieldHistory(rows), [rows]);

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={editPanel}
                        onChange={(_, next) => {
                            if (!next) return;
                            setEditPanel(next);
                            if (next === 'lastYear') setLastYearMounted(true);
                        }}
                    >
                        <ToggleButton value="invoices" sx={{ textTransform: 'none', px: 2 }}>Invoices</ToggleButton>
                        <ToggleButton value="lastYear" sx={{ textTransform: 'none', px: 2 }}>Last Year</ToggleButton>
                    </ToggleButtonGroup>
                    {editPanel === 'invoices' && (
                        <Typography variant="body2" color="text.secondary">
                            Editing {validCount} / {rows.length} rows · Enter next · Esc done · = balance · Ctrl+D duplicate · Ctrl+Enter save
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {editPanel === 'invoices' && (
                        <>
                            <TextField
                                size="small"
                                value={rowQuery}
                                placeholder="Date, description, voucher, account, amount"
                                onChange={(e) => setRowQuery(e.target.value)}
                                sx={{ width: 280 }}
                            />
                            {(searching || unbalancedOnly) && (
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                    {filteredRows.length} {filteredRows.length === 1 ? 'match' : 'matches'}
                                </Typography>
                            )}
                            <Tooltip title="Show only invoices where DR total ≠ CR total (empty rows excluded)">
                                <Chip
                                    size="small"
                                    label={unbalancedCount > 0
                                        ? `Unbalanced (${unbalancedCount})`
                                        : 'Unbalanced'}
                                    color={unbalancedCount > 0 ? 'error' : 'default'}
                                    variant={unbalancedOnly ? 'filled' : 'outlined'}
                                    onClick={() => setUnbalancedOnly((v) => !v)}
                                    sx={{ cursor: 'pointer', fontWeight: unbalancedOnly ? 600 : 400 }}
                                />
                            </Tooltip>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={showClientAlias}
                                        onChange={(e) => setShowClientAlias(e.target.checked)}
                                    />
                                }
                                label={<Typography variant="caption">Show client names</Typography>}
                                sx={{ mr: 0, ml: 0 }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={dupKeepDate}
                                        onChange={(e) => setDupKeepDate(e.target.checked)}
                                    />
                                }
                                label={<Typography variant="caption">Dup keep date</Typography>}
                                sx={{ mr: 0, ml: 0 }}
                            />
                            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addRow} sx={{ textTransform: 'none' }}>
                                Add Row
                            </Button>
                        </>
                    )}
                    <Button size="small" variant="outlined" onClick={onCancel} disabled={saving} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                        sx={{ textTransform: 'none' }}
                    >
                        Save
                    </Button>
                </Stack>
            </Stack>

            {lastYearMounted && (
                <Box sx={{ display: editPanel === 'lastYear' ? 'block' : 'none' }}>
                    <GlLastYearEditor
                        companyId={companyId}
                        annualId={annualId}
                        summaryPriorYear={summaryPriorYear}
                        columnDefs={columnDefs}
                        initialRemarks={summaryPriorYearRemarks}
                        onPriorChange={(prior) => { editedPriorRef.current = prior; }}
                        onRemarksChange={(next) => { remarksRef.current = next; }}
                    />
                </Box>
            )}

            {editPanel === 'invoices' && (
            <>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 320px)' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 30, fontWeight: 600 }}>#</TableCell>
                            <TableCell sx={{ minWidth: 100, fontWeight: 600 }}>Date</TableCell>
                            <TableCell sx={{ minWidth: 140, fontWeight: 600 }}>Description</TableCell>
                            <TableCell sx={{ minWidth: 90, fontWeight: 600 }}>Voucher No</TableCell>
                            <TableCell sx={{ minWidth: 90, fontWeight: 600 }}>Invoice No</TableCell>
                            <TableCell sx={{ minWidth: 100, fontWeight: 600 }}>Invoice Date</TableCell>
                            <TableCell sx={{ minWidth: 90, fontWeight: 600 }}>Cheque No</TableCell>
                            <TableCell sx={{ minWidth: 100, fontWeight: 600 }}>Details</TableCell>
                            <TableCell sx={{ minWidth: 280, fontWeight: 600 }}>DR</TableCell>
                            <TableCell sx={{ minWidth: 210, fontWeight: 600 }} align="right">Amount</TableCell>
                            <TableCell sx={{ minWidth: 280, fontWeight: 600 }}>CR</TableCell>
                            <TableCell sx={{ minWidth: 210, fontWeight: 600 }} align="right">Amount</TableCell>
                            <TableCell sx={{ width: 140 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pagedRows.map((row, i) => {
                            const rowIdx = pageStart + i;
                            if (editingRowKey === row.key) {
                                return (
                                    <EditRow
                                        key={row.key}
                                        row={row}
                                        rowIdx={rowIdx}
                                        columnOptions={columnOptions}
                                        columnMap={columnMap}
                                        typeByCode={typeByCode}
                                        onUpdateRow={updateRow}
                                        onUpdateDrCr={updateDrCr}
                                        onRemoveDrCr={removeDrCr}
                                        onAddDrCr={addDrCr}
                                        onBalanceRow={balanceRow}
                                        onDuplicate={duplicateRow}
                                        onRemoveRow={removeRow}
                                        onDone={() => setEditingRowKey(null)}
                                        onCancelEdit={() => setEditingRowKey(null)}
                                        onAdvanceAfterCr={addRow}
                                        isLastRow={row.key === rows[rows.length - 1]?.key}
                                        onDoneAndNext={doneAndNext}
                                        onSaveAll={() => { void handleSave(); }}
                                        canDelete={canDelete}
                                        fieldHistory={fieldHistory}
                                        periodFrom={auditDateMin}
                                        periodTo={auditDateMax}
                                    />
                                );
                            }
                            return (
                                <CompactRow
                                    key={row.key}
                                    row={row}
                                    rowIdx={rowIdx}
                                    columnMap={columnMap}
                                    resolveFallbackZh={fallbackZh}
                                    onEdit={setEditingRowKey}
                                    onDuplicate={duplicateRow}
                                    onRemoveRow={removeRow}
                                    canDelete={canDelete}
                                    showClientAlias={showClientAlias}
                                    isAliasVisible={isAliasVisible}
                                    onToggleRevealAlias={toggleRevealKey}
                                    unbalanced={isEditRowUnbalanced(row.drCrItems, columnMap)}
                                />
                            );
                        })}
                        {pagedRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={13} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                    {unbalancedOnly && !searching
                                        ? 'No unbalanced invoices'
                                        : searching
                                            ? 'No matching rows'
                                            : 'No rows'}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={filteredRows.length}
                page={safePage}
                onPageChange={(_, p) => {
                    setEditingRowKey(null);
                    setPageNum(p);
                }}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => {
                    setEditingRowKey(null);
                    setPageSize(parseInt(e.target.value, 10) || DEFAULT_PAGE_SIZE);
                    setPageNum(0);
                }}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                sx={{ mt: 0.5 }}
            />
            </>
            )}
        </Box>
    );
}
